<template>
  <Head title="Admin" />

  <AppLayout>
    <div class="container-fluid">
      <div class="row justify-content-center mx-4">
        <div class="col p-0">
          <div class="mb-3">
            <a href="/admin/sixtran/search" class="text-primary">← Back to Search</a>
          </div>
          <h1 class="mb-3">Run {{ run.sync_run_id }}</h1>

          <ul class="nav nav-tabs mb-4">
            <li class="nav-item">
              <button
                :class="['nav-link', { active: activeTab === 'overview' }]"
                @click="activeTab = 'overview'"
              >
                Overview
              </button>
            </li>
            <li class="nav-item">
              <button
                :class="['nav-link', { active: activeTab === 'workflow' }]"
                @click="activeTab = 'workflow'"
              >
                Workflow
              </button>
            </li>
            <li class="nav-item">
              <button
                :class="['nav-link', { active: activeTab === 'logs' }]"
                @click="activeTab = 'logs'; loadLogsIfNeeded()"
              >
                Logs
              </button>
            </li>
            <li class="nav-item">
              <button
                :class="['nav-link', { active: activeTab === 'bq' }]"
                @click="activeTab = 'bq'; loadBqIfNeeded()"
              >
                BigQuery
              </button>
            </li>
          </ul>

          <div v-show="activeTab === 'overview'" class="tab-content">
            <div class="card">
              <div class="card-body">
                <div class="row mb-3">
                  <div class="col-md-6">
                    <p><strong>Client:</strong> {{ run.client?.name ?? '—' }}</p>
                    <p><strong>Segment:</strong> {{ run.segment?.name ?? '—' }}</p>
                    <p><strong>Integration:</strong> {{ run.integration?.name ?? '—' }}</p>
                    <p><strong>Destination:</strong> {{ run.destination?.name ?? '—' }}</p>
                  </div>
                  <div class="col-md-6">
                    <p><strong>Status:</strong>
                      <span
                        :class="{
                          'badge bg-success': run.status === 'completed',
                          'badge bg-danger': run.status === 'failed',
                          'badge bg-warning': run.status === 'partial',
                          'badge bg-secondary': !['completed', 'failed', 'partial'].includes(run.status),
                        }"
                      >
                        {{ run.status }}
                      </span>
                    </p>
                    <p><strong>Started:</strong> {{ formatDate(run.started_at) }}</p>
                    <p><strong>Finished:</strong> {{ formatDate(run.finished_at) }}</p>
                    <p><strong>Records:</strong> {{ run.records_processed ?? '—' }} processed, {{ run.records_updated ?? '—' }} updated, {{ run.records_failed ?? '—' }} failed</p>
                  </div>
                </div>
                <div v-if="run.error_message" class="alert alert-danger">
                  <strong>Error:</strong> {{ run.error_message }}
                  <details v-if="run.error_details" class="mt-2">
                    <summary>Full error details</summary>
                    <pre class="mt-2 mb-0 p-2 bg-dark text-light rounded small">{{ run.error_details }}</pre>
                  </details>
                </div>
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'workflow'" class="tab-content">
            <div class="card">
              <div class="card-body">
                <p v-if="run.workflow_execution_id" class="text-muted">
                  Workflow execution ID captured for this run.
                </p>
                <p v-else class="text-muted">
                  Workflow execution ID is not captured for this run. Use the link and search terms below to find the
                  execution in GCP Workflows.
                </p>
                <a :href="workflowsUrl" target="_blank" rel="noopener" class="btn btn-outline-primary mb-3">
                  Open Workflows Executions in GCP
                </a>
                <div v-if="run.workflow_execution_id" class="mb-3">
                  <label class="form-label mb-1"><strong>Workflow execution ID:</strong></label>
                  <input type="text" readonly class="form-control form-control-sm font-monospace"
                    :value="run.workflow_execution_id" @click="$event.target.select()" aria-label="Text input">
                </div>
                <p class="mb-2"><strong>Copyable search terms:</strong></p>
                <div class="bg-light p-3 rounded font-monospace small">
                  <div>workflow_execution_id: {{ run.workflow_execution_id ?? '—' }}</div>
                  <div>run_id: {{ run.sync_run_id }}</div>
                  <div>segment_id: {{ run.segment?.id ?? '—' }}</div>
                  <div>destination: {{ run.destination?.name ?? '—' }}</div>
                  <div>started_at: {{ run.started_at ?? '—' }}</div>
                  <div>finished_at: {{ run.finished_at ?? '—' }}</div>
                </div>
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'logs'" class="tab-content">
            <div class="card">
              <div class="card-body">
                <button
                  v-if="!logsLoaded && !logsLoading"
                  type="button"
                  class="btn btn-primary mb-3"
                  @click="loadLogs"
                >
                  Load logs
                </button>
                <div v-if="logsLoading" class="text-muted">Loading logs...</div>
                <div v-else-if="logsError" class="alert alert-warning">{{ logsError }}</div>
                <div v-else-if="logsData">
                  <a
                    v-if="logsData.filter"
                    :href="`https://console.cloud.google.com/logs/query;query=${encodeURIComponent(logsData.filter)}?project=avid-ai`"
                    target="_blank"
                    rel="noopener"
                    class="btn btn-sm btn-outline-secondary mb-2"
                  >
                    Open in Logs Explorer
                  </a>
                  <div v-if="logsData.filter" class="mb-2">
                    <label class="form-label small">Copy filter:</label>
                    <input
                      type="text"
                      readonly
                      class="form-control form-control-sm font-monospace"
                      :value="logsData.filter"
                      @click="$event.target.select()"
                     aria-label="Copy filter:">
                  </div>
                  <div v-if="Array.isArray(logsData.entries) && logsData.entries.length === 0" class="text-muted">
                    No log entries found for this run.
                  </div>
                  <div v-else-if="logsData.error" class="alert alert-warning">
                    {{ logsData.error }}
                    <div v-if="logsData.filter" class="mt-2 small">Filter: {{ logsData.filter }}</div>
                  </div>
                  <div v-else class="log-entries">
                    <div
                      v-for="(entry, i) in (logsData.entries || [])"
                      :key="i"
                      class="log-entry border-bottom py-1 small"
                    >
                      <span class="text-muted">{{ formatDate(entry.timestamp) }}</span>
                      <span
                        :class="{
                          'badge bg-danger': entry.severity === 'ERROR',
                          'badge bg-warning': entry.severity === 'WARNING',
                          'badge bg-info': entry.severity === 'INFO',
                          'badge bg-secondary': !['ERROR', 'WARNING', 'INFO'].includes(entry.severity),
                        }"
                        class="ms-2"
                      >
                        {{ entry.severity }}
                      </span>
                      <span class="ms-2">{{ entry.message }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'bq'" class="tab-content">
            <div class="card">
              <div class="card-body">
                <button
                  v-if="!bqLoaded && !bqLoading"
                  type="button"
                  class="btn btn-primary mb-3"
                  @click="loadBq"
                >
                  Load BQ evidence
                </button>
                <div v-if="bqLoading" class="text-muted">Loading BigQuery evidence...</div>
                <div v-else-if="bqError" class="alert alert-warning">{{ bqError }}</div>
                <div v-else-if="bqData">
                  <h6>Sync State</h6>
                  <div v-if="bqData.sync_state?.error" class="alert alert-warning">
                    {{ bqData.sync_state.error }}
                  </div>
                  <div v-else-if="bqData.sync_state">
                    <p>Rows: {{ bqData.sync_state.count ?? '—' }} (table: {{ bqData.sync_state.table ?? '—' }})</p>
                    <div v-if="bqData.sync_state.sample?.length" class="table-responsive">
                      <table class="table table-sm">
                        <thead>
                          <tr>
                            <th v-for="(val, key) in bqData.sync_state.sample[0]" :key="key">{{ key }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(row, i) in bqData.sync_state.sample" :key="i">
                            <td v-for="(val, key) in row" :key="key">{{ typeof val === 'object' ? JSON.stringify(val) : val }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <h6 class="mt-4">Diff Sample</h6>
                  <div v-if="bqData.diff?.error || bqData.diff?.message" class="alert alert-info">
                    {{ bqData.diff?.message || bqData.diff?.error }}
                  </div>
                  <div v-else-if="bqData.diff?.rows?.length" class="table-responsive">
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th v-for="(val, key) in bqData.diff.rows[0]" :key="key">{{ key }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, i) in bqData.diff.rows" :key="i">
                          <td v-for="(val, key) in row" :key="key">{{ typeof val === 'object' ? JSON.stringify(val) : val }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="text-muted">
                    Diff is cleared after run completes; enable diff snapshot on failure to preserve evidence.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
export default {
  name: 'AdminSixtranRunShow',
  props: {
    run: {
      type: Object,
      required: true,
    },
    workflowsUrl: {
      type: String,
      default: 'https://console.cloud.google.com/workflows?project=avid-ai-webapp',
    },
  },
  data() {
    return {
      activeTab: 'overview',
      logsLoaded: false,
      logsLoading: false,
      logsData: null,
      logsError: null,
      bqLoaded: false,
      bqLoading: false,
      bqData: null,
      bqError: null,
    };
  },
  methods: {
    formatDate(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return d.toLocaleString();
    },
    loadLogsIfNeeded() {
      if (!this.logsLoaded && !this.logsLoading) {
        this.loadLogs();
      }
    },
    loadBqIfNeeded() {
      if (!this.bqLoaded && !this.bqLoading) {
        this.loadBq();
      }
    },
    loadLogs() {
      this.logsLoading = true;
      this.logsError = null;
      window.axios
        .get(`/admin/sixtran/runs/${this.run.sync_run_id}/logs`)
        .then((response) => {
          this.logsData = response.data;
          this.logsLoaded = true;
        })
        .catch((err) => {
          this.logsError = err?.response?.data?.message || err.message || 'Failed to load logs';
        })
        .finally(() => {
          this.logsLoading = false;
        });
    },
    loadBq() {
      this.bqLoading = true;
      this.bqError = null;
      window.axios
        .get(`/admin/sixtran/runs/${this.run.sync_run_id}/bq`)
        .then((response) => {
          this.bqData = response.data;
          this.bqLoaded = true;
        })
        .catch((err) => {
          this.bqError = err?.response?.data?.message || err.message || 'Failed to load BigQuery evidence';
        })
        .finally(() => {
          this.bqLoading = false;
        });
    },
  },
};
</script>
